import { useCallback, useEffect, useRef, useState } from 'react'

// Default cooldown duration: 1 minute (in milliseconds)
const DEFAULT_COOLDOWN_MS = 60 * 1000

// Manages independent cooldowns keyed by an id (e.g. a business rule id).
// Lets several execution points (massive apply + individual switch) share the
// same blocking state per key, so applying rule X also blocks rule X elsewhere.
const useKeyedCooldown = (durationMs = DEFAULT_COOLDOWN_MS) => {
  // Map of key -> true while that key is blocked
  const [cooldownKeys, setCooldownKeys] = useState({})
  // Pending timeout id per key, to clear it on restart / unmount
  const timeoutsRef = useRef({})

  // Starts (or restarts) the blocking period for one key or a list of keys
  const startCooldown = useCallback(
    (keys) => {
      const keyList = (Array.isArray(keys) ? keys : [keys]).filter(
        (key) => key !== undefined && key !== null,
      )
      if (keyList.length === 0) return

      setCooldownKeys((prev) => {
        const next = { ...prev }
        keyList.forEach((key) => {
          next[key] = true
        })
        return next
      })

      keyList.forEach((key) => {
        if (timeoutsRef.current[key]) clearTimeout(timeoutsRef.current[key])
        timeoutsRef.current[key] = setTimeout(() => {
          setCooldownKeys((prev) => {
            const next = { ...prev }
            delete next[key]
            return next
          })
          delete timeoutsRef.current[key]
        }, durationMs)
      })
    },
    [durationMs],
  )

  // Whether the given key is currently blocked
  const isCoolingDown = useCallback((key) => !!cooldownKeys[key], [cooldownKeys])

  // Clean up all pending timeouts when the component unmounts
  useEffect(() => {
    const timeouts = timeoutsRef.current
    return () => {
      Object.values(timeouts).forEach((id) => clearTimeout(id))
    }
  }, [])

  return { isCoolingDown, startCooldown }
}

export default useKeyedCooldown
